/**
 * Format a pre-computed percentage value for display.
 * Handles both number and string inputs.
 *
 * @param value - The percentage value (already computed, e.g., 45.3)
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string like "45.3%" or "0%"
 */
export const formatPercentage = (value: number | string, decimals: number = 1): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return "0%";
  return `${num.toFixed(decimals)}%`;
};

export const formatPercentFromRatio = (value: number, total: number, decimals?: number): string => {
  if (total === 0) return "0%";
  return formatPercentage((value / total) * 100, decimals);
};

/**
 * Format a number as currency in Catalan/Spanish locale
 * Format: 3.454,56 € (dots for thousands, comma for decimals)
 */
export const formatCurrency = (
  amount: string | number,
  decimals: number = 2,
  currency: string = "EUR",
): string => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return decimals === 0 ? "0 €" : "0,00 €";
  // Use ca-ES (Catalan) locale which properly groups thousands at 1.000+
  // Format: 3.454,56 € (same as Spanish but with consistent grouping)
  return new Intl.NumberFormat("ca-ES", {
    style: "currency",
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(numAmount);
};

/**
 * Format currency in compact form (e.g., 50k €, 1.2M €)
 */
export const formatCurrencyCompact = (value: number | string): string => {
  const numValue = typeof value === "string" ? Number(value) : value;
  if (isNaN(numValue)) return "0 €";
  const absValue = Math.abs(numValue);
  const sign = numValue < 0 ? "-" : "";

  if (absValue >= 1000000) {
    return sign + (absValue / 1000000).toFixed(1).replace(".0", "") + "M €";
  }
  if (absValue >= 1000) {
    return sign + (absValue / 1000).toFixed(0) + "k €";
  }
  return (
    sign +
    new Intl.NumberFormat("es-ES", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(absValue) +
    " €"
  );
};

/**
 * Format a number with locale grouping (e.g., 1.234)
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat("es-ES").format(value);
};

/**
 * Format investment UUID to reference format
 * @param uuid - Investment UUID
 * @returns Formatted reference like "#INV-1234"
 */
export const formatInvestmentReference = (uuid: string | null): string => {
  if (!uuid) return "-";
  const shortId = uuid.substring(0, 4).toUpperCase();
  return `#INV-${shortId}`;
};

/**
 * Format a number string for display with Spanish thousand separators
 * Converts internal format (1234.56) to display format (1.234,56)
 * Preserves exact decimal places from the input (unlike formatNumberInput)
 */
export const formatNumberDisplay = (value: string): string => {
  if (!value) return "";
  const [integerPart = "", ...rest] = value.split(".");
  return [integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "."), ...rest].join(",");
};

/**
 * Format a number string for display with Spanish locale during typing.
 * Converts internal format (1234.56) to display format (1.234,56).
 *
 * Preserves exactly what the user typed — including trailing zeros in the
 * decimal part ("46.0" → "46,0") and a lone "0" ("0" → "0"). Does NOT go
 * through parseFloat so digit-typing flow stays lossless.
 */
export const formatNumberInput = (value: string | null | undefined): string => {
  if (value == null || value === "") return "";

  const dotIdx = value.indexOf(".");
  const integerStr = dotIdx === -1 ? value : value.slice(0, dotIdx);
  const decimalStr = dotIdx === -1 ? null : value.slice(dotIdx + 1);

  if (integerStr !== "" && !/^\d+$/.test(integerStr)) return "";
  if (decimalStr !== null && !/^\d*$/.test(decimalStr)) return "";

  let displayInt = "";
  if (integerStr !== "") {
    // Strip leading zeros but keep a lone "0" (so "0" stays "0", "007" → "7")
    const trimmed = integerStr.replace(/^0+(?=\d)/, "");
    displayInt = trimmed.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  if (dotIdx === -1) return displayInt;

  // Decimal part: preserve exactly what was typed, capped at 2 digits.
  const decimal = (decimalStr ?? "").slice(0, 2);
  return `${displayInt},${decimal}`;
};

/**
 * Parse Spanish formatted number back to standard decimal format.
 * Converts display format (1.234,56) to internal format (1234.56).
 *
 * Tolerant to extraneous characters (€, spaces, etc.) — strips anything
 * that isn't a digit, comma or dot before parsing.
 */
export const parseFormattedNumber = (formatted: string): string => {
  if (!formatted) return "";

  const cleaned = formatted.replace(/[^\d,.]/g, "");
  const commaIdx = cleaned.indexOf(",");

  if (commaIdx === -1) {
    // No decimal — drop thousand-separator dots
    return cleaned.replace(/\./g, "");
  }

  const integerPart = cleaned.slice(0, commaIdx).replace(/\./g, "");
  const decimalPart = cleaned
    .slice(commaIdx + 1)
    .replace(/[.,]/g, "")
    .slice(0, 2);

  return `${integerPart}.${decimalPart}`;
};

export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES");
  } catch {
    return "-";
  }
};

export const formatDateTime = (dateString: string | null | undefined) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Format date in long format (e.g., "15 de enero de 2024")
 */
export const formatDateLong = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

/**
 * Format date relative to now (e.g., "Hoy", "Ayer", "Hace 3 días")
 * @param dateString - ISO date string
 * @param t - Translation function for i18n support
 */
export const formatRelativeDate = (
  dateString: string | null | undefined,
  t: (key: string, fallback?: string) => string,
): string => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t("hub.activity.today", "Hoy");
    } else if (diffDays === 1) {
      return t("hub.activity.yesterday", "Ayer");
    } else if (diffDays < 7) {
      const text = t("hub.activity.days_ago", "Hace {{count}} días");
      return text.replace("{{count}}", String(diffDays));
    } else {
      return formatDate(dateString);
    }
  } catch {
    return "-";
  }
};

/**
 * Get initials from a name (e.g., "John Doe" -> "JD")
 */
export const getInitials = (name: string | null | undefined): string => {
  if (!name) return "??";
  const words = name.trim().split(" ").filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]?.[0]}${words[1]?.[0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

export const getFileExtension = (contentType: string): string => {
  const typeMap: Record<string, string> = {
    "application/pdf": "PDF",
    "image/png": "PNG",
    "image/jpeg": "JPG",
    "image/jpg": "JPG",
  };
  return typeMap[contentType] || contentType.split("/")[1]?.toUpperCase() || "FILE";
};

/**
 * Format current date with weekday (e.g., "Miércoles, 26 de febrero de 2026")
 * Capitalizes the first letter, lowercases the rest.
 */
export const formatCurrentDateLong = (locale: string): string => {
  const now = new Date();
  const formatted = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};

/**
 * Format day + short month without year (e.g., "15 ene")
 */
export const formatDateDayMonth = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
  });
};

export const formatDocumentDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/**
 * Format date with leading zeros (dd/mm/yyyy)
 * Accepts both Date objects and ISO date strings
 */
export const formatDatePadded = (dateInput: string | Date | null | undefined): string => {
  if (!dateInput) return "-";
  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

/**
 * Format date + time with short month name (e.g., "15 ene 2024, 14:30")
 */
export const formatDateTimeShort = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
